/**
 * Backend stores money as `bigint` micros (millionths of a USD).
 * The SPA renders dollars-and-cents with two decimals by default.
 *
 *   formatMicros("12345678") → "$12.35"
 *   formatMicros("0")        → "$0.00"
 *
 * Negative values render with a leading minus.
 */

export function formatMicros(
  micros: string | number | bigint,
  options: { minorDigits?: number; currency?: string } = {},
): string {
  const minor = options.minorDigits ?? 2;
  const currency = options.currency ?? 'USD';
  let big: bigint;
  try {
    big = typeof micros === 'bigint' ? micros : BigInt(micros);
  } catch {
    return '—';
  }
  const negative = big < 0n;
  if (negative) big = -big;

  // 1_000_000 micros = 1 unit. To render with `minor` digits we
  // truncate the fractional micro precision to 10^(6-minor).
  const divisor = 10n ** BigInt(6 - minor);
  const scaled = big / divisor;
  const major = scaled / 10n ** BigInt(minor);
  const fraction = scaled % 10n ** BigInt(minor);
  const fractionStr = fraction.toString().padStart(minor, '0');
  const majorStr = major.toString();
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: minor,
    maximumFractionDigits: minor,
  });
  const numeric = Number(`${majorStr}.${fractionStr}`);
  if (!Number.isFinite(numeric)) return '—';
  return `${negative ? '-' : ''}${formatter.format(numeric)}`;
}

/**
 * Brief 185 — adaptive precision so a sub-cent charge isn't lost to "$0.00".
 * Renders 2 decimals normally, but bumps to 6 when the 2-decimal form would
 * round a non-zero amount to zero (the cost-plus transparency moment — a 65µ
 * LLM call reads "$0.000065", not "$0.00").
 */
export function formatMicrosAdaptive(micros: string | number | bigint): string {
  let big: bigint;
  try {
    big = typeof micros === 'bigint' ? micros : BigInt(micros);
  } catch {
    return '—';
  }
  const abs = big < 0n ? -big : big;
  const minorDigits = abs > 0n && abs < 10_000n ? 6 : 2;
  return formatMicros(big, { minorDigits });
}

export function MoneyAmount({
  micros,
  minorDigits,
  currency,
  className,
}: {
  micros: string | number | bigint;
  minorDigits?: number;
  currency?: string;
  className?: string;
}) {
  return (
    <span className={className}>
      {formatMicros(micros, {
        ...(minorDigits !== undefined ? { minorDigits } : {}),
        ...(currency !== undefined ? { currency } : {}),
      })}
    </span>
  );
}
